import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {keys} from 'ts-transformer-keys';
import memoize from 'memoize-one';
import _ from 'lodash';

import {Button} from 'primereact/button';
import {Card} from 'primereact/card';
import {Column} from 'primereact/column';

import metaModel from '~/shared/src/utilities/metaModel';
import {__} from '~/shared/src/utilities/messageCatalog';
import User from '~/shared/src/model/User';
import Item from '~/shared/src/ci/Item';
import {ReferenceOnlyAttributesForCreation} from '~/shared/src/ci/Reference';
import {
    buildDataDescriptor,
    ItemDataDescriptor
} from '~/shared/src/utilities/dataStructure';
import {BaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {LoadMetaDataRowData} from '~/shared/src/utilities/modules/overview';
import {
    ColumnConfig,
    ColumnDefinition
} from '~/shared/src/validationSchemes/modules/overview';
import {LoadReferencePanelDataParameters} from '~/shared/src/utilities/modules/finder';
import objectUtils from '~/shared/src/utilities/objectUtils';

import AddReferenceDialog, {
    ComponentProps as AddReferenceDialogComponentProps
} from '~/frontend/src/components/base/ItemFinder/detail/ReferencePanel/AddReferenceDialog';
import Toolbar, {
    ComponentProps as ToolbarComponentProps
} from '~/frontend/src/components/base/ItemFinder/detail/ReferencePanel/Toolbar';
import DataTable, {
    defaultProps as dataTableDefaultProps,
    ComponentProps as DataTableComponentProps,
    AllProps as DataTableAllProps,
    Props as DataTableProps,
    CellEditorData as DataTableCellEditorData,
    ActionColumns,
    CellEditorErrors
} from '~/frontend/src/components/base/DataTable/index';
import {
    getItemFinderLegendbar
} from '~/frontend/src/components/base/ItemFinder/common/utilities';
import ReferenceDetailPanel, {
    ComponentProps as ReferenceDetailPanelComponentProps
} from '~/frontend/src/components/base/ItemFinder/detail/ReferencePanel/ReferenceDetailPanel';

import {
    initialize,
    preInitialize,
    extractViewPropsFromProps
} from '~/frontend/src/components/base/ItemFinder/detail/ReferencePanel/utilities';
import {getReferenceForDataDescriptorFromRowData} from '~/frontend/src/utilities/dataUtils';
import {StoreState} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';

import * as PanelManagerActions from '~/frontend/src/actions/panelManager';
import {OpenPanel} from '~/frontend/src/actions/panelManager/openPanel';
import {OpenPanelForItemType} from '~/frontend/src/actions/panelManager/openPanelForItemType';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {SetParams} from '~/frontend/src/actions/panel/common/setParams';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import loadData, {LoadData} from '~/frontend/src/actions/data/loadData';
import * as ElusiveDataActions from '~/frontend/src/actions/elusiveData';
import {SetElusiveDataAction} from '~/frontend/src/actions/elusiveData';
import * as DataActions from '~/frontend/src/actions/data';
import {AddReference} from '~/frontend/src/actions/data/addReference';
import {DeleteReference} from '~/frontend/src/actions/data/deleteReference';
import {AddReferencedItem} from '~/frontend/src/actions/data/addReferencedItem';


const Actions = Object.assign( {}, PanelManagerActions, PanelActions, DataActions, { loadData }, ElusiveDataActions ) as any;
type SetParam = <K extends keyof ComponentProps>( panelKey : string, paramKey : K, paramValue : ComponentProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof ComponentProps ) => UnsetParamAction;
type SetElusiveData = ( panelKey : string, data : PanelElusiveData ) => SetElusiveDataAction;


const referenceDetailPanelComponentProps = keys<ReferenceDetailPanelComponentProps>(),
      toolbarComponentProps              = keys<ToolbarComponentProps>(),
      dataTableComponentProps            = keys<DataTableComponentProps>();

const historyStatusId = {
    CFG : metaModel?.[ 'CFG' ]?.getStatus( 'history' )?.id,
    DATA: metaModel?.[ 'DATA' ]?.getStatus( 'history' )?.id
};

const deletedStatusId = {
    CFG : metaModel?.[ 'CFG' ]?.getStatus( 'deleted' )?.id,
    DATA: metaModel?.[ 'DATA' ]?.getStatus( 'deleted' )?.id
};

export const resetStatusIdCaches = () => {
    historyStatusId.CFG = metaModel[ 'CFG' ].getStatus( 'history' )?.id;
    historyStatusId.DATA = metaModel[ 'DATA' ].getStatus( 'history' )?.id;
    deletedStatusId.CFG = metaModel[ 'CFG' ].getStatus( 'deleted' )?.id;
    deletedStatusId.DATA = metaModel[ 'DATA' ].getStatus( 'deleted' )?.id;
};


export const defaultProps = {
    ...dataTableDefaultProps,

    /**
     * The Id of the parent item to show referenced items for.
     */
    parentItemId: 0 as number,

    /**
     * The ItemTypeName to show items for.
     */
    itemTypeName: '' as string,

    /**
     * Show the legend
     */
    legend: false as boolean,

    /**
     * Is the overview is editable.
     */
    editable: true as boolean
};

export type DefaultProps = typeof defaultProps;

type Props = DataTableProps & {

    /**
     * The itemTypeof the item to show the references for.
     */
    parentItemTypeName : string;

    /**
     * The transformer for the panels (component) state.
     * NOTE: Have to been set (in opposition to be a default) to be included in the state anyway!
     */
    panelTransformer? : string;

    /**
     * Parameters for deep linking of a property, referenced ItemType or reference property
     */
    deepLinkProps? : {
        referenceId : number;
        referenceProperty : Required<ReferenceDetailPanelComponentProps>[ 'deepLinkProps' ][ 'property' ];
    };

    /**
     * The props for sub panelsc
     */
    subPanelProps? : any;
};

type PanelElusiveData = PanelElusiveDataState & DataTableCellEditorData & PanelErrorData;

type StateProps = {

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;

    /**
     * The elusive data (not persisted by redux store) for this component.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

type ActionProps = {
    // panelManager actions
    openPanel : OpenPanel;
    openPanelForItemType : OpenPanelForItemType;

    // common panel actions
    setParam : SetParam;
    setParams : SetParams;
    unsetParam : UnsetParam;

    // data actions
    loadData : LoadData;

    // data actions
    addReference : AddReference;
    deleteReference : DeleteReference;
    addReferencedItem : AddReferencedItem;

    // elusiveData actions
    setElusiveData : SetElusiveData;
};


export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps & ActionProps;


type State = {
    value : DataTableComponentProps[ 'value' ];
    totalRecords : DataTableComponentProps[ 'totalRecords' ];
} & PanelErrorData;


// TODO: add rowClick Handler to open generic detail view


/**
 * ReferencePanel
 */
class ReferencePanel extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    /**
     * YOONITE lifecycle method
     */
    static onPreInitializePanel( props : ComponentProps & { user : User } ) : ComponentProps {
        return preInitialize( {
            ...props
        } );
    }

    /**
     * YOONITE lifecycle method
     */
    static onInitializePanel( props : ComponentProps & { user : User } ) : ComponentProps {
        return initialize( props );
    }

    static searchRequestUid = 'loadOverviewData' as string;

    static viewSelectorModule = 'FinderDetailReference';

    /**
     * REDUX stuff
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            value       : undefined,
            totalRecords: undefined
        };

        // memoize some methods for performance
        this.getActionColumns = memoize( this.getActionColumns );
        this.getItemFinderLegendbar = memoize( this.getItemFinderLegendbar );
        this.isInsertable = memoize( this.isInsertable );

        // bind event handlers
        this.onAddDialogAddReference = this.onAddDialogAddReference.bind( this );
        this.onAddDialogCheckReference = this.onAddDialogCheckReference.bind( this );
        this.onRowClick = this.onRowClick.bind( this );
        this.rowClass = this.rowClass.bind( this );
        this.rowState = this.rowState.bind( this );

        this.processDeepLink = this.processDeepLink.bind( this );
        this.loadData = this.loadData.bind( this );
        this.restoreDeleteCellRenderer = this.restoreDeleteCellRenderer.bind( this );
        this.rowExpansionTemplate = this.rowExpansionTemplate.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const {
                  dataContext,
                  dataStructure,
                  parentItemId,
                  columnDefinitions,
                  columns,
                  elusiveData
              }                       = props,
              retVal : Partial<State> = {
                  value       : elusiveData?.metaData?.rowData,
                  totalRecords: elusiveData?.metaData?.total
              },
              dataObject              = dataStructure?.dataStructure?.[ 0 ],
              cellEditorErrors        = ( elusiveData?.cellEditorErrors ?? {} ) as CellEditorErrors,
              cellEditorErrorKeys     = Object.keys( cellEditorErrors ),
              cellEditorErrorCount    = cellEditorErrorKeys.length;

        let addedRowData : LoadMetaDataRowData[] = [];

        // add inserted references
        if ( elusiveData?.data?.references ) {
            for ( const referenceId of Object.keys( elusiveData.data.references ) ) {
                if ( referenceId < 0 ) {
                    const reference = global.data?.[ dataContext ]?.references[ referenceId ];

                    if ( reference ) {
                        let item : Item | undefined;

                        if ( reference.srcItemId === parentItemId ) {
                            item = reference.destItem;
                        }
                        else {
                            item = reference.srcItem;
                        }

                        if ( item ) {
                            const data : LoadMetaDataRowData = {};

                            for ( const column of columns ) {
                                const columnDefinition = columnDefinitions.find( colDef => colDef.field === column.field );

                                if ( columnDefinition ) {
                                    const dataDescriptor = columnDefinition.dataDescriptor;

                                    let value : any = '';

                                    if ( dataDescriptor.dataObject?.id === dataObject?.id && !dataDescriptor.aggregate ) {
                                        // NOTE: properties and referenceProperties not necessary for rendering, only for unique and ordering in BE
                                        if ( dataDescriptor.type === 'item' ) {
                                            if ( dataDescriptor.attribute === 'itemTypeName' ) {
                                                value = item.itemTypeId;
                                            }
                                            else if ( dataDescriptor.attribute === 'statusName' ) {
                                                value = item.statusId;
                                            }
                                            else {
                                                value = item[ dataDescriptor.attribute ];
                                            }
                                        }
                                        else if ( dataDescriptor.type === 'reference' ) {
                                            if ( dataDescriptor.attribute === 'referenceTypeName' || dataDescriptor.attribute === 'referenceSetName' ) {
                                                value = reference.referenceSetId;
                                            }
                                            else if ( dataDescriptor.attribute === 'statusName' ) {
                                                value = reference.statusId;
                                            }
                                            else if ( dataDescriptor.attribute === 'referenceEnd' || dataDescriptor.attribute === 'referenceTypeNameWithDirection' ) {
                                                value = reference.srcItemId === parentItemId ? reference.destItemId : reference.srcItemId;
                                            }
                                            else {
                                                value = reference[ dataDescriptor.attribute ];
                                            }
                                        }
                                    }

                                    data[ column.field ] = value;
                                }
                            }

                            if ( Object.keys( data ).length ) {
                                data.referenceId = referenceId;
                                addedRowData.push( data );
                            }
                        }
                    }
                }
            }

            if ( addedRowData.length ) {
                addedRowData.sort( ( val1, val2 ) => {
                    if ( val1.referenceId > val2.referenceId ) {
                        return 1;
                    }
                    else if ( val1.referenceId < val2.referenceId ) {
                        return -1;
                    }
                    else {
                        return 0;
                    }
                } );

                if ( elusiveData?.metaData?.rowData ) {
                    addedRowData = addedRowData.concat( elusiveData.metaData.rowData );
                }

                if ( !_.isEqual( addedRowData, retVal.value ) ) {
                    retVal.value = addedRowData;
                    retVal.totalRecords = addedRowData.length;
                }
            }
        }

        // errors
        if ( cellEditorErrorCount ) {
            retVal.errorData = {
                count  : cellEditorErrorCount,
                tooltip: [],
                message: []
            };

            for ( const cellId of cellEditorErrorKeys ) {
                const cellError        = cellEditorErrors[ cellId ],
                      columnIndex      = columns.findIndex( ( col : ColumnConfig ) => col.field === cellError.field ) + 1,
                      columnDefinition = columnDefinitions.find( ( colDef : ColumnDefinition ) => colDef.field === cellError.field ) as ColumnDefinition;

                if ( columnIndex ) {
                    const title    = __( 'YN.base.frontend.dataTable.error.detail.header', {
                              columnIndex : String( columnIndex ),
                              columnHeader: __( columnDefinition.header ?? '' ),
                              rowIndex    : String( cellError.rowIndex )
                          } ),
                          messages = cellError.messages.map( message => <>{message}<br/></> );

                    retVal.errorData.tooltip!.push( <div className="yn-tooltip-subheader">{title}</div> );
                    retVal.errorData.tooltip!.push( <>{messages}</> );

                    if ( retVal.errorData.message!.length ) {
                        retVal.errorData.message!.push( <hr className="yn-error-ruler"/> );
                    }
                    retVal.errorData.message!.push( <div className="yn-error-subtitle">{title}</div> );
                    retVal.errorData.message!.push( <div className="yn-error-message">{messages}</div> );
                }
            }
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.processDeepLink();
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate() {
        this.processDeepLink();
    }

    /**
     * REDUX stuff
     */
    shouldComponentUpdate( nextProps : AllProps, nextState : State ) : boolean {
        const changedState = objectUtils.getDiffKeys(
            this.props,
            nextProps,
            [
                'actionColumns',
                'cellEditorErrors',
                'columns',
                'columnDefaults',
                'columnDefinitions',
                'dataStructure',
                'elusiveData',
                'filters',
                'fixFilters',
                'footer',
                'rowClass',
                'rowExpansionTemplate',
                'rowGroupHeaderTemplate',
                'rowGroupFooterTemplate',
                'rowState',
                'rowStyling',
                'searchColumns',
                'sorters',
                'user',
                'xhrRequestsForComponent'
            ]
        );

        if ( changedState.includes( 'errorData' ) ) {
            this.props.setElusiveData( this.props.uid, { errorData: nextState.errorData } );
        }

        return true;
    }


    /* event handlers *********************************************************************************************************************/

    /**
     * Handler for action columns rowAction
     *
     * @param {'delete' | 'restore'} type
     * @param {LoadMetaDataRowData} rowData
     */
    onRestoreDeleteAction( type : 'delete' | 'restore', rowData : LoadMetaDataRowData ) {
        const {
                  uid,
                  dataContext,
                  schema
              }           = this.props,
              referenceId = this.getReferenceIdFromRowData( rowData ),
              rowIndex    = this.state.value?.findIndex( vRowData => JSON.stringify( vRowData ) === JSON.stringify( rowData ) ),
              reference   = global.data[ dataContext ].references[ referenceId ];

        if ( reference ) {
            // clear all editValues for row
            const {
                      columnDefinitions,
                      elusiveData
                  }                = this.props,
                  cellEditorValues = { ...elusiveData?.cellEditorValues ?? {} };

            if ( rowIndex ) {
                for ( const columnDefinition of columnDefinitions ) {
                    delete cellEditorValues[ DataTable.getCellId( rowIndex, columnDefinition.field ) ];
                }
                this.props.setElusiveData( uid, { cellEditorValues } );
            }

            switch ( type ) {
                case 'delete':
                    this.props.deleteReference( dataContext, referenceId, true, [ uid ], true );
                    break;

                case 'restore':
                    this.props.addReference( dataContext, reference, schema, [ uid ] );
                    break;
            }
        }
    }

    /**
     * Handler for AddDialog addReference event
     *
     * @param {ReferenceOnlyAttributesForCreation} referenceAttributes
     * @param {Item} targetItem
     *
     * @private
     */
    onAddDialogAddReference( referenceAttributes : ReferenceOnlyAttributesForCreation, targetItem : Item ) {
        const {
                  uid,
                  dataContext,
                  schema,
                  addReferencedItem
              } = this.props;

        addReferencedItem( dataContext, targetItem.toJSON(), referenceAttributes, schema, [ uid ] );
    }

    /**
     * Handler for AddDialog checkReference event
     *
     * @param {ReferenceOnlyAttributesForCreation} referenceAttributes
     *
     * @private
     */
    onAddDialogCheckReference( referenceAttributes : ReferenceOnlyAttributesForCreation ) : ReturnType<Required<AddReferenceDialogComponentProps>[ 'onCheckReference' ]> {
        const retVal : ReturnType<Required<AddReferenceDialogComponentProps>[ 'onCheckReference' ]> = {
                  creatable: true
              },
              {
                  dataContext,
                  schema,
                  elusiveData
              }                                                                                     = this.props,
              referenceSet                                                                          = metaModel[ schema ].getReferenceSet( referenceAttributes.referenceSetId );

        // check inserted references
        if (
            elusiveData?.data?.references
            && (
                referenceSet?.cardinality === '1:1'
                || referenceSet?.cardinality === '1:n'
                || referenceSet?.cardinality === 'n:1'
            )
        ) {
            for ( const referenceId of Object.keys( elusiveData.data.references ) ) {
                if ( referenceId < 0 ) {
                    if ( global.data[ dataContext ].references[ referenceId ]?.referenceSetId === referenceSet.id ) {
                        retVal.creatable = false;
                        retVal.reason = __( 'YN.base.finder.reference.check.insertable.cardinality.violation', { cardinality: referenceSet.cardinality } );
                        break;
                    }
                }
            }
        }

        return retVal;
    }

    /**
     * Handler for dataTables rowClick
     *
     * @private
     */
    onRowClick( field : string, rowData : LoadMetaDataRowData, event : React.MouseEvent<HTMLElement> ) {
        const {
                  dataContext,
                  dataStructure,
                  editMode
              }                    = this.props,
              itemIdDataDescriptor = buildDataDescriptor( { attributePath: 'item.id' }, dataStructure.dataStructure![ 0 ] ) as ItemDataDescriptor,
              itemId               = rowData[ itemIdDataDescriptor?.alias ],
              item                 = global.data[ dataContext ]?.items[ itemId ];

        if ( !editMode && itemId ) {
            if ( !item.isDeleted() ) {
                void this.props.openPanelForItemType(
                    this.props.schema,
                    this.props.itemTypeName,
                    {
                        schema   : this.props.schema,
                        status   : this.props.status, /* !== 'all' ? this.props.status : 'all-non-history',*/
                        timestamp: this.props.timestamp,
                        timezone : this.props.timezone,
                        //
                        rootItemId: itemId,
                        //
                        rawMode               : this.props.rawMode,
                        showAdditionalRawNames: this.props.showAdditionalRawNames
                    },
                    undefined,
                    event
                );
            }
            else {
                this.props.openPanel( {
                    panelType: 'FinderDetail',
                    //
                    schema   : this.props.schema,
                    status   : this.props.status,
                    timestamp: this.props.timestamp,
                    timezone : this.props.timezone,
                    //
                    rootItemId: itemId,
                    //
                    rawMode               : this.props.rawMode,
                    showAdditionalRawNames: this.props.showAdditionalRawNames
                } );
            }
        }

        return true;
    }

    /**
     * Handler for dataTable rowClass call
     *
     * @param {LoadMetaDataRowData} rowData
     *
     * @return {{ [ className : string ] : boolean }}
     *
     * @private
     */
    rowClass( rowData : LoadMetaDataRowData ) : { [ className : string ] : boolean } {
        const retVal : { [ className : string ] : boolean } = {},
              {
                  schema,
                  dataContext,
                  dataStructure
              }                                             = this.props;

        if ( dataStructure?.dataStructure?.[ 0 ] ) {
            const referenceTypeDataDescriptor = buildDataDescriptor( { attributePath: 'reference.referenceTypeName' }, dataStructure.dataStructure[ 0 ] );

            if ( referenceTypeDataDescriptor ) {
                const reference             = getReferenceForDataDescriptorFromRowData( dataContext, referenceTypeDataDescriptor, rowData ),
                      referenceSetId        = reference?.referenceSetId ?? 0,
                      referencePropertySets = metaModel[ schema ].getReferencePropertySetsForReferenceSet( referenceSetId );

                if ( !referencePropertySets.length ) {
                    retVal[ 'yn-rowexpander-hidden' ] = true;
                }
            }
        }

        return retVal;
    }

    /**
     * Handler for dataTable rowState call
     *
     * @param {LoadMetaDataRowData} rowData
     *
     * @return {'inserted' | 'modified' | 'deleted' | undefined}
     *
     * @private
     */
    rowState( rowData : LoadMetaDataRowData ) : 'inserted' | 'modified' | 'deleted' | undefined {
        const {
                  dataContext
              }           = this.props,
              referenceId = this.getReferenceIdFromRowData( rowData ),
              reference   = global.data[ dataContext ].references[ referenceId ];

        let retVal : 'inserted' | 'modified' | 'deleted' | undefined;

        if ( reference ) {
            if ( reference.isModified() && reference.isDeleted() ) {
                retVal = 'deleted';
            }
            else if ( reference.isNew() ) {
                retVal = 'inserted';
            }
        }

        return retVal;
    }

    /**
     * Handler for dataTable rowExpansionTemplate call
     * @param rowData
     */
    rowExpansionTemplate( rowData : LoadMetaDataRowData ) : JSX.Element {
        let retVal : JSX.Element = <></>;

        const {
                  uid,
                  dataContext,
                  parentItemId,
                  deepLinkProps,
                  subPanelProps
              }                       = this.props,
              referenceId             = this.getReferenceIdFromRowData( rowData.rowData ),
              referenceDetailPanelKey = `referenceDetail-${referenceId}`;

        let referenceDetailSubPanelProps = subPanelProps?.[ referenceDetailPanelKey ] ?? {};

        // apply deepLinkProps if
        if ( deepLinkProps?.referenceProperty ) {
            referenceDetailSubPanelProps = {
                ...referenceDetailSubPanelProps,
                deepLinkProps: {
                    property: deepLinkProps.referenceProperty
                }
            };

            this.props.setParams( referenceDetailPanelKey, referenceDetailSubPanelProps );
            this.props.unsetParam( uid, 'deepLinkProps' );
        }

        const referenceDetailPanelProps = {
            ..._.pick( this.props, referenceDetailPanelComponentProps/*keys<ReferenceDetailPanelComponentProps>()*/ ) as ReferenceDetailPanelComponentProps,
            ...referenceDetailSubPanelProps
        };

        let rowObject;

        if ( parentItemId ) {
            rowObject = global.data[ dataContext ].references[ referenceId ];
        }

        if ( rowObject ) {
            retVal = <ReferenceDetailPanel
                {...referenceDetailPanelProps}

                uid={`${uid}_${referenceDetailPanelKey}`}
                reference={rowObject}
                style={{ paddingLeft: '56px', maxWidth: '100rem' }}
            />;
        }

        return retVal;
    }


    /* private ****************************************************************************************************************************/

    /**
     * Processes given deepLinkProps.
     *
     * @private
     */
    processDeepLink() {
        if ( this.props.deepLinkProps ) {
            const {
                      uid,
                      deepLinkProps
                  }        = this.props,
                  rowIndex = this.state.value?.findIndex( vRowData => this.getReferenceIdFromRowData( vRowData ) === deepLinkProps.referenceId );

            let clearDeepLinkProps = true,
                expandedRow : LoadMetaDataRowData | undefined;

            if ( rowIndex ) {
                const dataTableRows = document.querySelectorAll( `#${uid}_dataTable .p-datatable-tbody tr` ) as NodeListOf<HTMLElement>,
                      row           = dataTableRows?.[ rowIndex ];

                if ( row ) {
                    row.scrollIntoView( {
                        behavior: 'smooth',
                        block   : 'center',
                        inline  : 'nearest'
                    } );
                }

                expandedRow = this.state.value?.[ rowIndex ];

                clearDeepLinkProps = false;
            }

            if ( expandedRow ) {
                this.props.setParam( uid, 'expandedRows', [ expandedRow ] );
            }

            if ( clearDeepLinkProps ) {
                this.props.unsetParam( uid, 'deepLinkProps' );
            }
        }
    }

    /**
     * Creates the add reference button for action columns header.
     *
     * @param {string} uid
     * @param {string} dataContext
     * @param {Schema} schema
     * @param {number} parentItemId
     * @param {string} parentItemTypeName
     * @param {string} itemTypeName
     * @param {boolean} rawMode
     * @param {boolean} showAdditionalRawNames
     * @param {User} user
     *
     * @return {JSX.Element[]}
     */
    getActionColumns( uid : string, dataContext : string, schema : Schema, parentItemId : number, parentItemTypeName : string, itemTypeName : string, rawMode : boolean, showAdditionalRawNames : boolean, editable : boolean, user : User ) : ActionColumns {
        const retVal : ActionColumns = {},
              insertable             = this.isInsertable( schema, parentItemTypeName, itemTypeName, user ),
              width : number         = 28;

        let field : string;

        if ( editable && parentItemId ) {
            field = 'restoreDelete';

            retVal[ field ] = {
                width,
                column: <Column
                    header={<AddReferenceDialog
                        key={`${uid}_addDialog`}

                        uid={uid}
                        dataContext={dataContext}

                        schema={schema}
                        itemId={parentItemId}
                        itemTypeName={parentItemTypeName}
                        targetItemTypeName={itemTypeName}
                        disabled={!insertable}
                        rawMode={rawMode}
                        showAdditionalRawNames={showAdditionalRawNames}
                        onAddReference={this.onAddDialogAddReference}
                        onCheckReference={this.onAddDialogCheckReference}
                        className="yn-action-button yn-green-button"
                    />}
                    body={this.restoreDeleteCellRenderer}

                    field={field}
                    bodyClassName="yn-nowrap-column yn-nopadding-column yn-action-column"
                    headerClassName="yn-header-action-column"
                    filterHeaderClassName="yn-header-action-column"
                    reorderable={false}
                    resizeable={false}
                />
            };
        }

        field = 'expander';
        retVal[ field ] = {
            width,
            column: <Column
                expander

                field={field}
                bodyClassName="yn-nowrap-top-column yn-nopadding-column yn-action-column"
                headerClassName="yn-header-action-column"
                filterHeaderClassName="yn-header-action-column"
                reorderable={false}
                resizeable={false}
            />
        };

        return retVal;
    }

    /**
     * Memoizable wrapper.
     *
     * @private
     */
    getItemFinderLegendbar( uid : string, schema : Schema, rawMode : boolean, showAdditionalRawNames : boolean, legend : boolean ) : JSX.Element {
        return getItemFinderLegendbar( uid, schema, rawMode, showAdditionalRawNames, legend );
    }

    /**
     * Is a new reference is insertable.
     *
     * @param {Schema} schema
     * @param {string} parentItemTypeName
     * @param {string} itemTypeName
     * @param {User} user
     *
     * @return {boolean}
     */
    isInsertable( schema : Schema, parentItemTypeName : string, itemTypeName : string, user : User ) {
        const permission        = 'insert',
              genericPermission = 'nonBlocked';

        let retVal = false;

        if ( user ) {
            const referenceSets = metaModel[ schema ].getReferenceSetsForItemTypes( parentItemTypeName, itemTypeName );

            for ( const referenceSet of referenceSets ) {
                if (
                    referenceSet.id > 0
                    && user.hasPermissionOnReferenceSet( permission, referenceSet.id, schema )
                    && user.hasGenericPermissionOnReferenceSet( genericPermission, referenceSet.id, schema )
                ) {
                    retVal = true;
                    break;
                }
            }
        }

        return retVal;
    }

    /**
     * load the overview data from the backend
     *
     * @param {Object} props
     *
     * @return {boolean}
     *
     * @private
     */
    loadData( props : DataTableAllProps ) : boolean {
        const {
                  uid,
                  dataContext,
                  status,
                  timestamp,
                  parentItemId,
                  itemTypeName,
                  parentItemTypeName,
                  xhrRequestsForComponent,
                  elusiveData
              }                                                                 = this.props,
              {
                  schema,
                  timezone,
                  //
                  columns,
                  filters,
                  fixFilters,
                  sorters,
                  page,
                  rows
              }                                                                 = props,
              lastParams                                                        = elusiveData?.lastParams ?? '',
              params : BaseRequestParameters & LoadReferencePanelDataParameters = {
                  schema   : schema,
                  status   : status,
                  timestamp: timestamp,
                  timezone : timezone,

                  parentItemId      : parentItemId,
                  itemTypeName      : itemTypeName,
                  parentItemTypeName: parentItemTypeName,

                  columns: columns.map( column => column.field ),
                  filters: [],
                  sorters: sorters,

                  start: ( page - 1 ) * rows,
                  limit: rows
              },
              nonEmptyFilters                                                   = [];

        let retVal : boolean = false;

        // filter out empty filters
        if ( fixFilters ) {
            for ( let i = fixFilters.length - 1; i >= 0; i-- ) {
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                if ( fixFilters[ i ].value || fixFilters[ i ].operator === 'empty' || fixFilters[ i ].operator === 'notEmpty' ) {
                    nonEmptyFilters.push( fixFilters[ i ] );
                }
            }
        }
        if ( filters ) {
            for ( let i = filters.length - 1; i >= 0; i-- ) {
                if (
                    !nonEmptyFilters.find( filter => filter.field === filters[ i ].field )
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                    && ( filters[ i ].value || filters[ i ].operator === 'empty' || filters[ i ].operator === 'notEmpty' )
                ) {
                    nonEmptyFilters.push( filters[ i ] );
                }
            }
        }
        params.filters = nonEmptyFilters;

        // search parameters or page changed
        if ( lastParams !== 'requested' && ( JSON.stringify( params ) !== JSON.stringify( lastParams ) || !global.data[ dataContext ] ) ) {

            retVal = true;

            // abort running load
            if ( xhrRequestsForComponent?.[ ReferencePanel.searchRequestUid ] ) {
                xhr.abort( uid, ReferencePanel.searchRequestUid );
            }

            // clear elusive data and mark backend requested
            this.props.setElusiveData( uid, {
                lastParams: 'requested'
            } );

            this.props.loadData(
                'finder/loadReferencePanelData',
                dataContext,
                uid,
                ReferencePanel.searchRequestUid,
                schema,
                {
                    params,
                    extraMetaDataKeys: [ 'rowData', 'rowDataMapping', 'total' ]
                },
                true,
                this.processDeepLink
            );
        }

        return retVal;
    }

    /**
     * Gets the parent references id for given rowData
     *
     * @param {LoadMetaDataRowData} rowData
     *
     * @return {number}
     */
    getReferenceIdFromRowData( rowData : LoadMetaDataRowData ) : number {
        let retVal = 0;

        const {
                  dataStructure,
                  elusiveData
              } = this.props;

        if ( dataStructure?.dataStructure?.[ 0 ] ) {
            const referenceIdDataDescriptor = buildDataDescriptor( { attributePath: 'reference.id' }, dataStructure.dataStructure[ 0 ] );

            if ( referenceIdDataDescriptor ) {
                const referenceIdField = referenceIdDataDescriptor.alias;

                retVal = rowData[ referenceIdField ];
                if ( !retVal && elusiveData?.metaData?.rowDataMapping?.[ referenceIdField ] ) {
                    retVal = rowData[ elusiveData.metaData.rowDataMapping[ referenceIdField ] ];
                }
            }
        }

        return retVal;
    }

    /**
     * renderer for action column
     *
     * @param {{ renderCache : Map<string, JSX.Element | string | undefined> }} columnRendererProps
     * @param {LoadMetaDataRowData} rowData
     *
     * @return {JSX.Element | string | undefined}
     */
    restoreDeleteCellRenderer( data : { rowData : LoadMetaDataRowData } ) : JSX.Element | string | undefined {
        let retVal : JSX.Element | string | undefined = '';

        const {
                  dataContext,
                  schema,
                  user
              }           = this.props,
              rowData     = data.rowData,
              referenceId = this.getReferenceIdFromRowData( rowData ),
              reference   = global.data[ dataContext ].references[ referenceId ],
              statusId    = reference?.statusId;

        if ( historyStatusId[ schema ] === undefined ) {
            resetStatusIdCaches();
        }

        if ( statusId ) {
            const isHistory = statusId === historyStatusId[ schema ],
                  isDeleted = statusId === deletedStatusId[ schema ];

            let icon,
                className,
                onClickHandler,
                enabled = false;

            if ( reference.isDeleted() ) {
                icon = 'pi pi-plus-circle';
                className = 'yn-action-button yn-green-button';

                onClickHandler = this.onRestoreDeleteAction.bind( this, 'restore', rowData );

                if (
                    reference
                    && ( (
                            isHistory
                            && user?.hasPermissionOnReferenceSet( 'update', reference.referenceSetId, schema )
                            && user?.hasGenericPermissionOnReferenceSet( 'nonBlocked', reference.referenceSetId, schema )
                        )
                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                        || (
                            isDeleted
                            && user?.hasPermissionOnReferenceSet( 'insert', reference.referenceSetId, schema )
                            && user?.hasGenericPermissionOnReferenceSet( 'nonBlocked', reference.referenceSetId, schema )
                        )
                    )
                ) {
                    enabled = true;
                }
            }

            // delete
            else {
                icon = 'pi pi-minus-circle';
                className = 'yn-action-button yn-red-button';

                onClickHandler = this.onRestoreDeleteAction.bind( this, 'delete', rowData );

                if (
                    reference
                    && user?.hasPermissionOnReferenceSet( 'delete', reference.referenceSetId, schema )
                    && user?.hasGenericPermissionOnReferenceSet( 'nonBlocked', reference.referenceSetId, schema )
                ) {
                    enabled = true;
                }
            }

            if ( className ) {
                retVal = <Button
                    onClick={
                        enabled
                            ? onClickHandler
                            : () => {
                            }
                    }

                    disabled={!enabled}

                    icon={icon}
                    className={`${className}`}
                />;
            }
        }

        return retVal;
    }

    /**
     * REDUX stuff
     */
    render() {
        const {
                  uid,
                  dataContext,
                  schema,
                  parentItemId,
                  itemTypeName,
                  parentItemTypeName,
                  rawMode,
                  showAdditionalRawNames,
                  legend,
                  editable,
                  xhrRequestsForComponent,
                  elusiveData,
                  user
              }              = this.props,
              toolbarProps   = {
                  ..._.pick( this.props, toolbarComponentProps /*keys<ToolbarComponentProps>()*/ ) as ToolbarComponentProps,

                  key: `${uid}_toolbar`,

                  viewSelectorPanelProps: { ...extractViewPropsFromProps( this.props ) },

                  className: 'yn-panel-toolbar-border-none-bottom'
              },
              dataTableProps = {
                  ..._.pick( this.props, dataTableComponentProps /*keys<DataTableComponentProps>()*/ ) as DataTableComponentProps,

                  key: `${uid}_dataTable`,
                  id : `${uid}_dataTable`,

                  loading          : !!xhrRequestsForComponent,
                  value            : this.state.value,
                  totalRecords     : this.state.totalRecords,
                  valueFieldMapping: elusiveData?.metaData?.rowDataMapping ?? {},

                  reload              : this.loadData,
                  onRowClick          : this.onRowClick,
                  actionColumns       : this.getActionColumns( uid, dataContext, schema, parentItemId, parentItemTypeName, itemTypeName, rawMode, showAdditionalRawNames, editable, user! ),
                  rowClass            : this.rowClass,
                  rowState            : this.rowState,
                  rowExpansionTemplate: this.rowExpansionTemplate,
                  search              : true,
                  noMultiLineCells    : true,
                  footer              : this.getItemFinderLegendbar( uid, schema, rawMode, showAdditionalRawNames, legend ),

                  className: 'yn-flex yn-finder yn-datatable-border-none-top'
              };

        return <div className="yn-card-content-panel yn-flex-layout-parent p-0">
            <Card className="yn-flex-layout-parent" style={{ width: '100%', maxWidth: '100%' }}>
                <Toolbar
                    {...toolbarProps}
                />
                <DataTable
                    {...dataTableProps}
                />
            </Card>
        </div>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        xhrRequestsForComponent: undefined,
        elusiveData            : undefined,
        user                   : undefined
    };

    // get the running xhr request belonging to this component (uid)
    if ( state.xhr?.[ ownProps.uid ] ) {
        retVal.xhrRequestsForComponent = state.xhr[ ownProps.uid ];
    }

    // get elusiveData
    // for the data (items and references) belonging to this component
    if ( state.elusiveData?.[ ownProps.dataContext ] ) {
        retVal.elusiveData = {
            ...retVal.elusiveData ?? {},
            ...state.elusiveData[ ownProps.dataContext ]
        };
    }
    // for all other infromation belonging to this component
    if ( state.elusiveData?.[ ownProps.uid ] ) {
        retVal.elusiveData = {
            ...retVal.elusiveData ?? {},
            ...state.elusiveData[ ownProps.uid ]
        };
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( ReferencePanel );
